<template>
  <Layout>
    <PageHeader :title="titleBody" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3 align-items-end">
              <div class="col-md-2">
                <label class="small text-muted mb-0">Data de</label>
                <input
                  v-model="filters.date_from"
                  type="date"
                  class="form-control form-control-sm"
                  @change="loadPage(1)"
                />
              </div>
              <div class="col-md-2">
                <label class="small text-muted mb-0">Data até</label>
                <input
                  v-model="filters.date_to"
                  type="date"
                  class="form-control form-control-sm"
                  @change="loadPage(1)"
                />
              </div>
              <div class="col-md-2">
                <label class="small text-muted mb-0">Ação</label>
                <select v-model="filters.action" class="form-control form-control-sm" @change="loadPage(1)">
                  <option value="">Todas</option>
                  <option value="created">Criado</option>
                  <option value="updated">Atualizado</option>
                  <option value="deleted">Excluído</option>
                  <option value="authorized">Autorizado</option>
                  <option value="approved">Aprovado</option>
                </select>
              </div>
              <div class="col-md-3">
                <label class="small text-muted mb-0">Usuário(s)</label>
                <Multiselect
                  v-model="filters.selectedUsers"
                  :options="userOptions"
                  :multiple="true"
                  :close-on-select="false"
                  :clear-on-select="false"
                  :preserve-search="true"
                  placeholder="Buscar por ID ou nome..."
                  :loading="usersLoading"
                  :internal-search="false"
                  :searchable="true"
                  label="name"
                  track-by="id"
                  @search-change="searchUsers"
                  @open="onUsersOpen"
                >
                  <template slot="option" slot-scope="scope">
                    <span class="small">#{{ scope.option.id }}</span> — {{ scope.option.name }}
                  </template>
                </Multiselect>
              </div>
              <div class="col-md-2">
                <label class="small text-muted mb-0">ID do registro</label>
                <input
                  v-model.number="filters.auditable_id"
                  type="number"
                  class="form-control form-control-sm"
                  placeholder="Opcional"
                  @keyup.enter="loadPage(1)"
                />
              </div>
              <div class="col-md-2 text-md-right mt-2 mt-md-0">
                <button type="button" class="btn btn-primary btn-sm" :disabled="loading" @click="loadPage(1)">
                  <i class="ri-search-line"></i> Filtrar
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm ml-1" :disabled="loading" @click="resetFilters">
                  Limpar
                </button>
              </div>
            </div>

            <div v-if="error403" class="alert alert-warning">
              {{ error403 }}
            </div>
            <div v-if="error422" class="alert alert-danger">
              {{ error422 }}
            </div>

            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2 mb-0">Carregando auditoria...</p>
            </div>

            <div class="row mb-3 align-items-center">
              <div class="col-sm-12 col-md-6">
                <label class="d-inline-flex align-items-center mb-0 small text-muted">
                  Exibir&nbsp;
                  <b-form-select v-model="perPage" size="sm" :options="pageOptions" class="mx-1" @change="loadPage(1)"></b-form-select>
                  &nbsp;registros
                </label>
              </div>
              <div class="col-sm-12 col-md-6 mt-2 mt-md-0">
                <div class="d-flex justify-content-md-end">
                  <b-form-input
                    v-model="search"
                    type="search"
                    size="sm"
                    placeholder="Pesquisar nos registros..."
                    class="mr-2 audit-search"
                    @keyup.enter="loadPage(1)"
                  ></b-form-input>
                  <b-button size="sm" variant="outline-primary" :disabled="loading" @click="loadPage(1)">
                    <i class="ri-search-line"></i>
                  </b-button>
                </div>
              </div>
            </div>

            <b-table
              :items="rows"
              :fields="fields"
              :busy="loading"
              responsive
              hover
              bordered
              small
              show-empty
              empty-text="Nenhum registro de auditoria."
              no-local-sorting
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @sort-changed="onSortChanged"
            >
              <template #cell(created_at)="row">{{ formatDate(row.item.created_at) }}</template>
              <template #cell(user)="row">{{ row.item.user ? row.item.user.name : '—' }}</template>
              <template #cell(auditable_type)="row">{{ row.item.type_label || row.item.auditable_type_label || row.item.auditable_type }}</template>
              <template #cell(action)="row">{{ row.item.action_label || row.item.action }}</template>
              <template #cell(details)="row">
                <b-button variant="link" size="sm" class="p-0" @click="toggleDetail(row.item)">
                  {{ expandedId === row.item.id ? 'Ocultar' : 'Detalhes' }}
                </b-button>
              </template>
              <template #row-details="row">
                <div v-if="expandedId === row.item.id" class="bg-light small p-2">
                  <div v-if="detailSummaryItems(row.item).length" class="mb-2">
                    <div class="font-weight-bold mb-1 text-dark">Resumo</div>
                    <div v-for="item in detailSummaryItems(row.item)" :key="item.label" class="d-flex justify-content-between align-items-start">
                      <span class="text-muted pr-2">{{ item.label }}:</span>
                      <span class="text-right pl-2 text-dark" style="max-width:65%">{{ item.value }}</span>
                    </div>
                  </div>
                  <details v-if="hasValues(row.item)" class="mt-2">
                    <summary class="text-muted mb-2">Ver JSON</summary>
                    <div v-if="row.item.old_values && Object.keys(row.item.old_values).length" class="mb-2">
                      <strong>Valores anteriores:</strong>
                      <pre class="p-2 bg-white border rounded" style="max-height:180px;overflow:auto">{{ jsonPretty(row.item.old_values) }}</pre>
                    </div>
                    <div v-if="row.item.new_values && Object.keys(row.item.new_values).length">
                      <strong>Valores novos / metadados:</strong>
                      <pre class="p-2 bg-white border rounded" style="max-height:180px;overflow:auto">{{ jsonPretty(row.item.new_values) }}</pre>
                    </div>
                  </details>
                  <div v-if="!hasValues(row.item) && !detailSummaryItems(row.item).length" class="text-muted">Sem alterações de valores neste registro.</div>
                </div>
              </template>
            </b-table>

            <div v-if="meta.total > 0" class="d-flex justify-content-between align-items-center mt-3 flex-wrap">
              <span class="small text-muted">
                Total: {{ meta.total }} · Página {{ meta.current_page }} / {{ lastPage }}
              </span>
              <b-pagination
                v-model="currentPage"
                :total-rows="meta.total"
                :per-page="perPage"
                first-text="Primeiro"
                prev-text="Anterior"
                next-text="Próximo"
                last-text="Último"
                first-number
                last-number
                size="sm"
                class="mb-0"
                @change="loadPage"
              ></b-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '../../layouts/main'
import PageHeader from '@/components/page-header'
import Multiselect from 'vue-multiselect'
import { http } from '@/helpers/easyindustriaapi/config'
import appConfig from '@/app.config'

export default {
  page: {
    title: 'Auditoria',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: { Layout, PageHeader, Multiselect },
  computed: {
    lastPage() {
      return Math.max(1, this.meta.last_page || 1)
    }
  },
  data() {
    return {
      titleBody: 'Auditoria do sistema',
      items: [
        { text: 'Dashboard', href: '/' },
        { text: 'Usuários', href: '/' },
        { text: 'Auditoria', active: true }
      ],
      loading: true,
      rows: [],
      currentPage: 1,
      perPage: 20,
      pageOptions: [10, 20, 50, 100],
      search: '',
      sortBy: 'created_at',
      sortDesc: true,
      fields: [
        { key: 'created_at', label: 'Data/hora', sortable: true },
        { key: 'user', label: 'Usuário', sortable: false },
        { key: 'auditable_type', label: 'Entidade', sortable: true },
        { key: 'action', label: 'Ação', sortable: true },
        { key: 'auditable_id', label: 'Registro #', sortable: true },
        { key: 'details', label: '', sortable: false, class: 'text-center' }
      ],
      meta: { total: 0, current_page: 1, per_page: 20, last_page: 1 },
      filters: {
        action: '',
        auditable_id: '',
        date_from: '',
        date_to: '',
        selectedUsers: []
      },
      userOptions: [],
      usersLoading: false,
      searchUsersTimer: null,
      expandedId: null,
      detailRow: null,
      error403: null,
      error422: null
    }
  },
  mounted() {
    this.loadPage(1)
  },
  methods: {
    async loadPage(page = 1) {
      this.loading = true
      this.currentPage = page
      this.error403 = null
      this.error422 = null

      const userIds = (this.filters.selectedUsers || []).map((u) => u.id).filter(Boolean)
      try {
        const params = new URLSearchParams()
        params.set('per_page', String(this.perPage))
        params.set('page', String(page))
        params.set('sort_by', this.sortBy)
        params.set('sort_desc', this.sortDesc ? '1' : '0')
        if (this.search.trim()) params.set('search', this.search.trim())
        if (this.filters.action) params.set('action', this.filters.action)
        if (this.filters.auditable_id) params.set('auditable_id', String(this.filters.auditable_id))
        if (this.filters.date_from) params.set('date_from', this.filters.date_from)
        if (this.filters.date_to) params.set('date_to', this.filters.date_to)
        userIds.forEach((id) => params.append('user_ids[]', id))

        const { data } = await http.get('/audit-logs?' + params.toString())

        this.rows = (data.data || []).map((row) => ({ ...row, _showDetails: row.id === this.expandedId }))
        this.meta = {
          total: data.total || 0,
          current_page: data.current_page || 1,
          per_page: data.per_page || 20,
          last_page: data.last_page || 1
        }
        if (this.expandedId) {
          const still = this.rows.find((r) => r.id === this.expandedId)
          if (!still) this.expandedId = null
        }
      } catch (e) {
        this.rows = []
        const status = e.response && e.response.status
        const msg = (e.response && e.response.data && e.response.data.message) || ''
        if (status === 403) {
          this.error403 = msg || 'Acesso negado. Apenas super, partner ou admin, ou empresa inválida.'
        } else if (status === 422) {
          this.error422 = msg || 'Selecione a empresa de trabalho no sistema antes de consultar a auditoria.'
        }
      } finally {
        this.loading = false
      }
    },
    onSortChanged(ctx) {
      this.sortBy = ctx.sortBy || 'created_at'
      this.sortDesc = Boolean(ctx.sortDesc)
      this.loadPage(1)
    },
    resetFilters() {
      this.filters = {
        action: '',
        auditable_id: '',
        date_from: '',
        date_to: '',
        selectedUsers: []
      }
      this.search = ''
      this.sortBy = 'created_at'
      this.sortDesc = true
      this.loadPage(1)
    },
    async searchUsers(q) {
      if (this.searchUsersTimer) clearTimeout(this.searchUsersTimer)
      if (q === undefined || q === null) q = ''
      const term = String(q).trim()
      this.searchUsersTimer = setTimeout(async () => {
        this.usersLoading = true
        try {
          const params = new URLSearchParams()
          if (term) params.set('q', term)
          params.set('limit', '50')
          const { data } = await http.get('/audit-logs/users-search?' + params.toString())
          this.userOptions = (data && data.data) ? data.data : []
        } catch (e) {
          this.userOptions = []
        } finally {
          this.usersLoading = false
        }
      }, 280)
    },
    onUsersOpen() {
      if (this.userOptions.length === 0 && !this.usersLoading) {
        this.searchUsers('')
      }
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        const d = new Date(iso)
        return d.toLocaleString('pt-BR')
      } catch (e) {
        return iso
      }
    },
    jsonPretty(obj) {
      try {
        return JSON.stringify(obj, null, 2)
      } catch (e) {
        return String(obj)
      }
    },
    stripHtml(value) {
      if (value === null || value === undefined) return ''
      const s = String(value)
      // Remove tags HTML e normaliza &nbsp;
      return s.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '').trim()
    },
    toSimNao(value) {
      if (value === true || value === 1 || value === '1') return 'sim'
      if (value === false || value === 0 || value === '0') return 'não'
      return '—'
    },
    getFromValues(row, key) {
      const nv = (row && row.new_values) ? row.new_values : {}
      const ov = (row && row.old_values) ? row.old_values : {}
      // Prioriza new_values quando existir.
      return (nv && Object.prototype.hasOwnProperty.call(nv, key)) ? nv[key] : (ov ? ov[key] : undefined)
    },
    hasInValues(row, key) {
      const nv = (row && row.new_values) ? row.new_values : {}
      const ov = (row && row.old_values) ? row.old_values : {}
      return (nv && Object.prototype.hasOwnProperty.call(nv, key)) || (ov && Object.prototype.hasOwnProperty.call(ov, key))
    },
    hasValues(row) {
      const o = row && row.old_values && Object.keys(row.old_values).length
      const n = row && row.new_values && Object.keys(row.new_values).length
      return o || n
    },
    detailSummaryItems(row) {
      if (!row) return []
      const items = []

      const showPatient = this.hasInValues(row, 'pacient_id') || this.hasInValues(row, 'pacient_name')
      const showProfessional = this.hasInValues(row, 'professional_id') || this.hasInValues(row, 'professional_name')

      const patientName = this.getFromValues(row, 'pacient_name')
      const professionalName = this.getFromValues(row, 'professional_name')
      const patientId = this.getFromValues(row, 'pacient_id')
      const professionalId = this.getFromValues(row, 'professional_id')

      if (showPatient) {
        items.push({ label: 'paciente', value: patientName || (patientId ? `ID ${patientId}` : '—') })
      }
      if (showProfessional) {
        items.push({ label: 'profissional', value: professionalName || (professionalId ? `ID ${professionalId}` : '—') })
      }

      // Quando houver mudança de "professional_aware" (ciência do profissional)
      if (this.hasInValues(row, 'professional_aware')) {
        const aware = this.getFromValues(row, 'professional_aware')
        items.push({ label: 'professional_ciente', value: this.toSimNao(aware) })
      }

      // Se existir "desc"/"end"/"start_time" etc, tratamos como resumo de Recurrence (evolução)
      const hasEvolutionDesc = this.hasInValues(row, 'desc')
      const hasEvolutionDates = this.hasInValues(row, 'end') || this.hasInValues(row, 'start_time') || this.hasInValues(row, 'end_time')
      if (hasEvolutionDesc || hasEvolutionDates) {
        const id = this.getFromValues(row, 'id')
        const end = this.getFromValues(row, 'end')
        const desc = this.getFromValues(row, 'desc')
        const userName = row.user && row.user.name ? row.user.name : (this.getFromValues(row, 'user_id') || '—')
        const endTime = this.getFromValues(row, 'end_time')
        const agendaId = this.getFromValues(row, 'agenda_id')
        const createdAt = this.getFromValues(row, 'created_at')
        const startTime = this.getFromValues(row, 'start_time')
        const updatedAt = this.getFromValues(row, 'updated_at')
        const isCancelled = this.getFromValues(row, 'is_cancelled')

        const texto = this.stripHtml(desc)
        const cancelado = (isCancelled === true || isCancelled === 1 || isCancelled === '1') ? 'sim' : 'não'

        items.push({ label: 'código', value: (id !== undefined && id !== null) ? id : '—' })
        items.push({ label: 'fim', value: end || '—' })
        items.push({ label: 'texto', value: texto || '—' })
        items.push({ label: 'usuário', value: userName || '—' })
        items.push({ label: 'hora_fim', value: endTime || '—' })
        items.push({ label: 'codigo_agenda', value: agendaId || '—' })
        items.push({ label: 'criado', value: createdAt || '—' })
        items.push({ label: 'hora_inicio', value: startTime || '—' })
        items.push({ label: 'alterado', value: updatedAt || '—' })
        items.push({ label: 'cancelado', value: cancelado })
      }

      return items
    },
    toggleDetail(row) {
      if (this.expandedId === row.id) {
        this.expandedId = null
        this.detailRow = null
        this.$set(row, '_showDetails', false)
        return
      }
      this.rows.forEach((item) => this.$set(item, '_showDetails', item.id === row.id))
      this.expandedId = row.id
      this.detailRow = row
      this.$set(row, '_showDetails', true)
    }
  }
}
</script>
